import { Box, Paper, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { customDashboardAnalytics } from '../../customDashboardAnalytics'
import { customDashboardsHref } from '../../routing/routes'

// We use container queries here because we're in a variable width section on the dashboard (sidebar can be collapsed).
// Below this width the body wraps past two lines, so the banner drops the design min-height and
// the artwork. The height is a minimum, not fixed, so longer copy (translations, text zoom) grows
// the banner instead of being clipped by the Paper's overflow: hidden.
const NARROW_LAYOUT_BREAKPOINT = 800
const BANNER_MIN_HEIGHT = 144

// The design runs a chart line behind the copy: a 637×92 export anchored 45px in from the right and
// to the bottom edge, ending in a dot the export leaves out. The copy stays within the design's 504px
// text column so it doesn't run over the line's rise.
const ARTWORK_RIGHT = 45
const TEXT_MAX_WIDTH = 544
const DOT_SIZE = 10
// Centres the dot on the line's end point, (636, 1) in the export's coordinates.
const DOT_OFFSET = -4

export const CustomDashboardsBanner = () => {
  const { t } = useTranslation('reporting', { keyPrefix: 'custom-dashboards.home-banner' })
  const { user } = useAuthenticatedUser()

  if (!user.isAdmin) {
    return null
  }

  return (
    <Paper sx={{ maxWidth: '100%', mb: 3, containerType: 'inline-size', overflow: 'hidden', position: 'relative' }}>
      <Box
        sx={(theme) => ({
          position: 'absolute',
          right: ARTWORK_RIGHT,
          bottom: 0,
          [theme.containerQueries.down(NARROW_LAYOUT_BREAKPOINT)]: {
            display: 'none',
          },
        })}
      >
        <Box component="img" src="/app/assets/custom-breakdowns-banner.svg" alt="" sx={{ display: 'block' }} />
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: DOT_OFFSET,
            right: DOT_OFFSET,
            width: DOT_SIZE,
            height: DOT_SIZE,
            borderRadius: theme.shape['radius-full'],
            bgcolor: 'bg.brand.default',
          })}
        />
      </Box>
      <Stack
        direction="row"
        sx={(theme) => ({
          minHeight: BANNER_MIN_HEIGHT,
          [theme.containerQueries.down(NARROW_LAYOUT_BREAKPOINT)]: {
            minHeight: 'auto',
          },
        })}
      >
        {/* Positioned so it paints above the artwork, which comes first in the DOM. */}
        <Stack
          sx={(theme) => ({
            flexGrow: 1,
            px: 5,
            py: 4,
            justifyContent: 'center',
            position: 'relative',
            maxWidth: TEXT_MAX_WIDTH,
            [theme.containerQueries.down(NARROW_LAYOUT_BREAKPOINT)]: { maxWidth: 'none' },
          })}
        >
          <Typography variant="upsized-semiBold">{t('breakdowns-title')}</Typography>
          <Typography variant="body-regular" sx={{ color: 'text.muted' }}>
            {t('breakdowns-body')}
          </Typography>

          <Stack direction="row" gap={3} sx={{ pt: 2 }}>
            <InternalLinkButton
              variant="contained"
              color="primary"
              href={customDashboardsHref}
              onClick={() => customDashboardAnalytics.homeBannerClicked()}
            >
              {t('breakdowns-cta')}
            </InternalLinkButton>
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  )
}
